import { Injectable, computed, effect, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'preferredTheme';
const DEFAULT_THEME: Theme = 'light';

/**
 * The single choke point for the active colour theme, mirroring LanguageService.
 * Everything that reads or flips the theme goes through here rather than poking
 * the DOM directly.
 *
 * The stylesheet themes off a `dark` class on a container (see src/styles.css) —
 * light is the default. We keep that class on <html> in sync with the signal via
 * an effect, so the seed value is applied on construction and every later toggle
 * propagates automatically. The choice is persisted to localStorage and restored
 * on the next load; we intentionally do NOT follow prefers-color-scheme.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly theme = signal<Theme>(readStoredTheme());

  readonly isDark = computed(() => this.theme() === 'dark');

  constructor() {
    // Keep <html>.classList and the signal in lock-step. Runs once on creation
    // (applying the restored theme) and on every subsequent change.
    effect(() => {
      document.documentElement.classList.toggle('dark', this.theme() === 'dark');
    });
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage may be unavailable (private mode / locked down). The theme still
      // applies for this session; it just won't be remembered.
    }
  }

  toggle(): void {
    this.setTheme(this.isDark() ? 'light' : 'dark');
  }
}

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}
